import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx-js-style';

export interface ParsedSectionRow {
  master_value_name: string;
  fields: { [fieldKey: string]: any };
  remark?: string;
}

export interface ParsedSection {
  section_name: string;
  section_id?: number;
  rows: ParsedSectionRow[];
}

export interface ParsedPeriod {
  key: string;
  label: string;
  year?: string;
  month?: number;
  monthRaw?: string;
  date?: string;
  sections: ParsedSection[];
}

export interface ParsedSheet extends ParsedPeriod {
  formName?: string;
}

export interface ImportIssue {
  severity: 'error' | 'warning' | 'info';
  code: string;
  message: string;
  context?: any;
}

export interface ImportResult {
  success: boolean;
  fatal: boolean;
  message: string;
  parsed?: ParsedSheet;
  periods?: ParsedPeriod[];
  issues: ImportIssue[];
  error?: any;
}

const MONTH_NAME_MAP: { [key: string]: number } = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3,
  apr: 4, april: 4, may: 5, jun: 6, june: 6, jul: 7, july: 7,
  aug: 8, august: 8, sep: 9, sept: 9, september: 9,
  oct: 10, october: 10, nov: 11, november: 11, dec: 12, december: 12
};
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type HeaderType = 'year' | 'month' | 'date';
interface HeaderCell { type: HeaderType; raw: string; }
interface Banner { r: number; c: number; section: any; }
interface Ctx { year?: string; monthRaw?: string; month?: number; date?: string; }

@Injectable({ providedIn: 'root' })
export class FormExcelImport {

  parseExcel(fileBuffer: ArrayBuffer, formDefinition: any): ImportResult {
    const issues: ImportIssue[] = [];

    try {
      let wb: XLSX.WorkBook;
      try {
        wb = XLSX.read(fileBuffer, { type: 'array' });
      } catch (readErr) {
        return this.fatal('The uploaded file could not be read. It may be corrupted or not a valid Excel file.',
          'CORRUPT_FILE', 'XLSX.read failed.', String(readErr));
      }
      if (!wb?.SheetNames?.length) {
        return this.fatal('The Excel file has no sheets.', 'NO_SHEETS', 'Workbook has no sheet names.');
      }
      const ws = wb.Sheets[wb.SheetNames[0]];
      if (!ws) {
        return this.fatal(`Sheet "${wb.SheetNames[0]}" could not be opened.`, 'SHEET_MISSING', 'Sheet not found.');
      }
      const aoa: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: true, defval: '' });
      if (!aoa.length) {
        return this.fatal('The Excel sheet is empty.', 'EMPTY_SHEET', 'No rows found in the sheet.');
      }

      const forms: any[] = Array.isArray(formDefinition?.data)
        ? formDefinition.data
        : formDefinition?.sections ? [formDefinition] : [];
      if (!forms.length) {
        return this.fatal('No form definition was provided.', 'NO_FORM', 'formDefinition is missing or malformed.');
      }
      const form = forms[0];

      const matrixSections: any[] = Array.isArray(form.sections)
        ? form.sections.filter((s: any) =>
          String(s.layout_type || '').toLowerCase() === 'matrix' &&
          Array.isArray(s.matrix_rows) && s.matrix_rows.length > 0)
        : [];
      if (!matrixSections.length) {
        return this.fatal('The selected form has no matrix sections to import into.',
          'NO_SECTIONS', 'Form has no renderable matrix sections.');
      }

      const sectionByUpperName = new Map<string, any>();
      matrixSections.forEach(s =>
        sectionByUpperName.set(String(s.section_name || '').trim().toUpperCase(), s));

      const cell = (r: number, c: number): string => String(aoa[r]?.[c] ?? '').trim();
      const maxCols = aoa.reduce((m, row) => Math.max(m, row?.length ?? 0), 0);

      const banners: Banner[] = [];
      const typesPresent = new Set<HeaderType>();
      for (let r = 0; r < aoa.length; r++) {
        for (let c = 0; c < maxCols; c++) {
          const t = cell(r, c);
          if (!t) continue;
          const sec = sectionByUpperName.get(t.toUpperCase());
          if (sec) { banners.push({ r, c, section: sec }); continue; }
          const h = this.parseHeaderCell(t);
          if (h) typesPresent.add(h.type);
        }
      }

      if (!banners.length) {
        return this.fatal('No section banner was found in the Excel. Please check that your file matches the template.',
          'NO_SECTION_BANNER', 'No section banner found.');
      }

      const periods = new Map<string, ParsedPeriod>();
      const encounteredSectionNames = new Set<string>();

      for (const banner of banners) {
        const { c: c0, section } = banner;
        encounteredSectionNames.add(section.section_name);

        const ctx = this.findContext(aoa, banner.r, c0, typesPresent, cell, maxCols, issues);
        const key = `${ctx.year ?? ''}|${ctx.month ?? ctx.monthRaw ?? ''}|${ctx.date ?? ''}`;
        if (!periods.has(key)) {
          periods.set(key, {
            key,
            label: this.buildLabel(ctx),
            year: ctx.year,
            month: ctx.month,
            monthRaw: ctx.monthRaw,
            date: ctx.date,
            sections: []
          });
        }
        const period = periods.get(key)!;

        let sectionObj = period.sections.find(s => s.section_name === section.section_name);
        if (sectionObj) {
          issues.push({
            severity: 'warning',
            code: 'DUPLICATE_SECTION',
            message: `Section "${section.section_name}" appears more than once for ${period.label}. Rows will be merged.`
          });
        } else {
          sectionObj = { section_name: section.section_name, section_id: section.section_id, rows: [] };
          period.sections.push(sectionObj);
        }

        let hr = banner.r + 1;
        while (hr < aoa.length && !cell(hr, c0)) hr++;
        if (hr >= aoa.length || sectionByUpperName.has(cell(hr, c0).toUpperCase())) {
          issues.push({
            severity: 'warning',
            code: 'NO_COLUMN_HEADER',
            message: `No column header row found under section "${section.section_name}" (${period.label}).`
          });
          continue;
        }

        const firstHeaderLabel = cell(hr, c0).toLowerCase();
        const fields: any[] = Array.isArray(section.fields) ? section.fields : [];
        const fieldKeysByColumn: { [col: number]: string } = {};
        let lastCol = c0;

        for (let c = c0 + 1; c < maxCols; c++) {
          const rawHeader = cell(hr, c);
          const header = rawHeader.toLowerCase();
          if (!header) break;
          if (header === firstHeaderLabel) break;
          lastCol = c;

          if (header === 'remark' || header === 'remarks') {
            fieldKeysByColumn[c] = '__remark__';
            continue;
          }
          const match = fields.find(f =>
            String(f.field_label ?? '').trim().toLowerCase() === header ||
            String(f.field_key ?? '').trim().toLowerCase() === header);
          if (match) {
            fieldKeysByColumn[c] = match.field_key;
          } else {
            fieldKeysByColumn[c] = '';
            issues.push({
              severity: 'warning',
              code: 'UNKNOWN_FIELD',
              message: `Column "${rawHeader}" in section "${section.section_name}" (${period.label}) does not exist in the form. Its values will be ignored.`,
              context: { section: section.section_name, column: rawHeader, column_index: c }
            });
          }
        }


        for (let r = hr + 1; r < aoa.length; r++) {
          const masterName = cell(r, c0);
          if (!masterName) continue;
          const upperMaster = masterName.toUpperCase();

          if (sectionByUpperName.has(upperMaster) || this.parseHeaderCell(masterName)) break;
          if (upperMaster === 'TOTAL' || upperMaster.endsWith(' TOTAL') || upperMaster.includes('SUMMARY')) continue;

          const matrixRow = section.matrix_rows.find((mr: any) =>
            String(mr.master_value_name ?? '').trim().toLowerCase() === masterName.toLowerCase());

          if (!matrixRow) {
            issues.push({
              severity: 'warning',
              code: 'UNKNOWN_ROW',
              message: `Row "${masterName}" in section "${section.section_name}" (${period.label}) does not exist in the form. Skipped.`,
              context: { section: section.section_name, row: masterName, excel_row_number: r + 1 }
            });
            continue;
          }

          const sameName = (x: ParsedSectionRow) =>
            x.master_value_name.trim().toLowerCase() === matrixRow.master_value_name.trim().toLowerCase();
          if (sectionObj.rows.some(sameName)) {
            issues.push({
              severity: 'warning',
              code: 'DUPLICATE_ROW',
              message: `Row "${matrixRow.master_value_name}" appears more than once in section "${section.section_name}" (${period.label}). The later values will be used.`,
              context: { section: section.section_name, row: matrixRow.master_value_name }
            });
            sectionObj.rows = sectionObj.rows.filter(x => !sameName(x));
          }

          const rowData: ParsedSectionRow = { master_value_name: matrixRow.master_value_name, fields: {} };

          for (let c = c0 + 1; c <= lastCol; c++) {
            const key2 = fieldKeysByColumn[c];
            if (!key2) continue;
            const raw = aoa[r]?.[c];
            const isEmpty = raw === '' || raw == null || String(raw).trim() === '';

            if (key2 === '__remark__') {
              if (!isEmpty) rowData.remark = String(raw);
              continue;
            }

            const fieldDef = fields.find((f: any) => f.field_key === key2);

            if (isEmpty) {
              rowData.fields[key2] = 0;
              if (fieldDef?.is_required) {
                issues.push({
                  severity: 'warning',
                  code: 'REQUIRED_EMPTY',
                  message: `Required field "${fieldDef.field_label ?? key2}" was empty in row "${matrixRow.master_value_name}" (section "${section.section_name}", ${period.label}). Defaulted to 0.`,
                  context: { section: section.section_name, row: matrixRow.master_value_name, field: fieldDef.field_key }
                });
              }
            } else {
              const fieldType = String(fieldDef?.field_type ?? '').toLowerCase();
              if ((fieldType === 'number' || fieldType === 'currency') &&
                typeof raw !== 'number' &&
                isNaN(Number(String(raw).replace(/,/g, '')))) {
                issues.push({
                  severity: 'warning',
                  code: 'INVALID_NUMBER',
                  message: `Field "${fieldDef?.field_label ?? key2}" expected a number but got "${raw}" in row "${matrixRow.master_value_name}" (section "${section.section_name}", ${period.label}). Value kept as-is.`,
                  context: { section: section.section_name, row: matrixRow.master_value_name, field: fieldDef?.field_key, value: raw }
                });
              }
              rowData.fields[key2] = isNaN(raw) ? 0 : raw;
            }
          }
          sectionObj.rows.push(rowData);
        }
      }

      matrixSections.forEach(sec => {
        if (!encounteredSectionNames.has(sec.section_name)) {
          issues.push({
            severity: 'info',
            code: 'MISSING_SECTION',
            message: `Section "${sec.section_name}" was not found in the Excel. Its rows will remain empty.`
          });
        }
      });

      const periodList = Array.from(periods.values()).filter(p => p.sections.some(s => s.rows.length));
      if (!periodList.length) {
        return {
          success: false, fatal: true, issues, error: null,
          message: 'No valid data could be read from the Excel. Please make sure it matches the template.'
        };
      }

      periodList.sort((a, b) =>
        (Number(a.year ?? 0) - Number(b.year ?? 0)) ||
        ((a.month ?? 0) - (b.month ?? 0)) ||
        (Number(a.date ?? 0) - Number(b.date ?? 0)));

      if (periodList.length > 1) {
        issues.push({
          severity: 'info',
          code: 'MULTIPLE_PERIODS',
          message: `${periodList.length} periods found in the Excel: ${periodList.map(p => p.label).join(', ')}.`
        });
      }

      return {
        success: true,
        fatal: false,
        message: issues.some(i => i.severity === 'warning')
          ? 'Excel loaded, but with some warnings. Please review.'
          : 'Excel parsed successfully.',
        parsed: { ...periodList[0], formName: form.form_name },
        periods: periodList,
        issues
      };

    } catch (err) {
      console.error('[FormExcelImportService] parse error:', err);
      return {
        success: false, fatal: true,
        message: `Unexpected error while reading the Excel: ${err instanceof Error ? err.message : String(err)}`,
        issues: [{ severity: 'error', code: 'UNEXPECTED', message: 'Unexpected exception during parse.', context: String(err) }],
        error: err
      };
    }
  }

  private findContext(
    aoa: any[][], r: number, c: number, typesPresent: Set<HeaderType>,
    cell: (r: number, c: number) => string, maxCols: number, issues: ImportIssue[]
  ): Ctx {
    const ctx: Ctx = {};
    const got = new Set<HeaderType>();

    const apply = (h: HeaderCell) => {
      got.add(h.type);
      if (h.type === 'year') {
        const y = parseInt(h.raw, 10);
        if (!isNaN(y) && y >= 1900 && y <= 9999) ctx.year = String(y);
        else issues.push({
          severity: 'warning', code: 'INVALID_YEAR',
          message: `Year value "${h.raw}" is not valid (must be 1900–9999). Ignored.`
        });
      } else if (h.type === 'month') {
        ctx.monthRaw = h.raw;
        ctx.month = this.parseMonthValue(h.raw);
        if (ctx.month == null) issues.push({
          severity: 'warning', code: 'INVALID_MONTH',
          message: `Month value "${h.raw}" could not be understood. Expected 1–12 or a month name (e.g. "January").`
        });
      } else {
        const d = parseInt(h.raw, 10);
        if (!isNaN(d) && d >= 1 && d <= 31) ctx.date = String(d);
        else issues.push({
          severity: 'warning', code: 'INVALID_DATE',
          message: `Date value "${h.raw}" is not valid (must be 1–31). Ignored.`
        });
      }
    };

    for (let rr = r - 1; rr >= 0; rr--) {
      const h = this.parseHeaderCell(cell(rr, c));
      if (!h) continue;
      if (got.has(h.type)) continue;       // earlier sibling block -> ignore
      apply(h);
      if (h.type === 'year') break;        // year is the outermost header
    }

    typesPresent.forEach(type => {
      if (got.has(type)) return;
      for (let cc = c - 1; cc >= 0; cc--) {
        let found: HeaderCell | null = null;
        for (let rr = r - 1; rr >= 0; rr--) {
          const h = this.parseHeaderCell(cell(rr, cc));
          if (h && h.type === type) { found = h; break; }
        }
        if (found) { apply(found); break; }
      }
    });

    return ctx;
  }

  private parseHeaderCell(text: string): HeaderCell | null {
    const t = String(text ?? '').trim();
    let m = t.match(/^YEAR\s*[-:]\s*(.*)$/i);
    if (m) return { type: 'year', raw: m[1].trim() };
    m = t.match(/^MONTH\s*[-:]\s*(.*)$/i);
    if (m) return { type: 'month', raw: m[1].trim() };
    m = t.match(/^DATE\s*[-:]\s*(.*)$/i);
    if (m) return { type: 'date', raw: m[1].trim() };
    return null;
  }

  private buildLabel(ctx: Ctx): string {
    const parts: string[] = [];
    if (ctx.year) parts.push(ctx.year);
    if (ctx.month) parts.push(MONTH_LABELS[ctx.month - 1]);
    else if (ctx.monthRaw) parts.push(ctx.monthRaw);
    if (ctx.date) parts.push(`${ctx.date}`);
    return parts.join(' / ') || 'Data';
  }

  private fatal(message: string, code: string, detail: string, context?: any): ImportResult {
    return {
      success: false, fatal: true, message,
      issues: [{ severity: 'error', code, message: detail, context }]
    };
  }

  private parseMonthValue(raw: string): number | undefined {
    if (!raw) return undefined;
    const s = String(raw).trim().toLowerCase();
    if (!s) return undefined;

    if (/^\d+$/.test(s)) {
      const n = parseInt(s, 10);
      return n >= 1 && n <= 12 ? n : undefined;
    }
    const cleanedName = s.replace(/[^a-z]/g, '');
    if (MONTH_NAME_MAP[cleanedName] != null) return MONTH_NAME_MAP[cleanedName];
    for (const key of Object.keys(MONTH_NAME_MAP)) {
      if (new RegExp(`\\b${key}\\b`, 'i').test(s)) return MONTH_NAME_MAP[key];
    }
    const numMatch = s.match(/(?:^|[^\d])(\d{1,2})(?:[^\d]|$)/);
    if (numMatch) {
      const n = parseInt(numMatch[1], 10);
      if (n >= 1 && n <= 12) return n;
    }
    return undefined;
  }
}